import { api } from "./api";

export interface ToolDefinition {
    functionDeclarations: Array<{
        name: string;
        description: string;
        parameters: any;
    }>;
}

export interface CreateConversationOptions {
    system_prompt?: string;
    temperature?: number;
    max_output_tokens?: number;
    thinking_enabled?: boolean;
    thinking_budget?: number;
    tools?: ToolDefinition[];
}

export interface ConversationResponse {
    id: string;
}

export interface Part {
    text?: string;
    functionCall?: {
        name: string;
        args: Record<string, any>;
    };
    functionResponse?: {
        name: string;
        response: Record<string, any>;
    };
}

export interface MessageResponse {
    parts: Part[];
    metadata?: any;
}

export interface ContentBlock {
    text?: string;
    functionResponse?: {
        name: string;
        response: Record<string, any>;
    };
    [key: string]: any;
}

export const aiService = {
    /**
     * Starts a new stateful AI conversation.
     * @param options Configuration for the AI conversation (system prompt, temperature, tools, etc.)
     * @returns The generated conversation ID
     */
    createConversation(options?: CreateConversationOptions): Promise<ConversationResponse> {
        return api.post<ConversationResponse>("/ai/conversation", options || {});
    },

    /**
     * Creates a pre-configured AI conversation specifically tailored for editing blog posts.
     * Starts with a grounded system prompt and access to specific editing tools.
     * @returns The generated conversation ID
     */
    createEditorConversation(): Promise<ConversationResponse> {
        return api.post<ConversationResponse>("/ai/agents/editor", {});
    },

    /**
     * Creates a pre-configured AI conversation specifically tailored for photography assistance.
     * Starts with a grounded system prompt for a photographer AI service.
     * @returns The generated conversation ID
     */
    createPhotographerConversation(): Promise<ConversationResponse> {
        return api.post<ConversationResponse>("/ai/agents/photographer", {});
    },

    /**
     * Sends a simple text string to an existing conversation and retrieves the AI's response.
     * @param id The conversation ID
     * @param message Text message to send to the AI
     * @returns The newly generated conversation parts from the AI
     */
    sendMessage(id: string, message: string): Promise<MessageResponse> {
        return api.put<MessageResponse>(`/ai/conversation/${id}/message`, { message });
    },

    /**
     * Sends a complex, multi-part message to the conversation (useful for tool call responses, images, etc.).
     * @param id The conversation ID
     * @param contentBlocks Array of content block objects, e.g. functionResponse
     * @returns The newly generated conversation parts from the AI
     */
    sendContent(id: string, contentBlocks: ContentBlock[]): Promise<MessageResponse> {
        return api.put<MessageResponse>(`/ai/conversation/${id}/content`, { content_blocks: contentBlocks });
    },

    /**
     * Kills a conversation and removes it from the server's memory.
     * @param id The conversation ID
     */
    deleteConversation(id: string): Promise<void> {
        return api.delete<void>(`/ai/conversation/${id}`);
    }
};
